import { Dayjs } from 'dayjs';
import { observer } from 'mobx-react-lite';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Page } from '../../../../components/Page/Page';
import { SchoolEventModal } from '../../../../components/SchoolEvent/Modals/SchoolEventModal';
import { SchoolEventTable } from '../../../../components/SchoolEvent/Tables/SchoolEventTable';
import { useConfirm } from '../../../../hooks/useConfirm';
import { useMemorizedCacheTags } from '../../../../hooks/useMemorizedCacheTag';
import { usePermissionChecker } from '../../../../hooks/usePermissionChecker';
import {
  _RecurringEventDocument,
  _RecurringEventQuery,
  _RecurringEventQueryVariables,
  use_DeleteRecurrenceSchoolEventsMutation,
  use_DeleteSchoolEventsMutation,
} from '../../../../types/planung-graphql-client-defs';
import { showSuccessDeleteToast, showUserErrorToast } from '../../../../utils/toast';
import { urqlClient } from '../../../../utils/urqlClient';

export type SchoolEventType = {
  uuid: string;
  name: string;
  classUuids: string[];
  groupUuids: string[];
  subjectUuid: string;
  teacherUuids: string[];
  startDate: Dayjs;
  endDate: Dayjs;
  rrule: string | null;
  excludeHolidays: boolean;
  isClassbookRelevant: boolean;
};

export const PlanSchoolEvent = observer(() => {
  const { t } = useTranslation();
  const context = useMemorizedCacheTags(['SCHOOL_EVENT', 'EVENT']);
  const permissions = usePermissionChecker();

  const [loading, setLoading] = useState<boolean>(false);
  const [modalOpen, setModalOpen] = useState<boolean>(false);
  const [selectedUuid, setSelectedUuid] = useState<string | null>(null);

  const [, deleteEvent] = use_DeleteSchoolEventsMutation();
  const [, deleteRecurringEvent] = use_DeleteRecurrenceSchoolEventsMutation();

  const { confirm, ConfirmationDialog } = useConfirm();

  function handleAdd() {
    setSelectedUuid(null);
    setModalOpen(true);
  }

  function handleEdit(uuid: string) {
    setSelectedUuid(uuid);
    setModalOpen(true);
  }

  function handleDuplicate(uuid: string) {
    console.log('TODO: implement');
  }

  async function handleDelete(uuids: string[]) {
    const result = await confirm({
      title: t('schoolEvents.delete.title'),
      message: t('schoolEvents.delete.warning', { count: uuids.length }),
    });
    if (!result) return;
    setLoading(true);
    const { error } = await deleteEvent({ uuids: { uuids: uuids } }, context);
    if (error) {
      showUserErrorToast({ error });
    } else {
      showSuccessDeleteToast();
    }
    setLoading(false);
  }

  async function handleDeleteRecurring(recurrenceUuid: string | null) {
    if (!recurrenceUuid) return;
    const { data } = await urqlClient
      .query<_RecurringEventQuery, _RecurringEventQueryVariables>(
        _RecurringEventDocument,
        {
          where: {
            uuid: recurrenceUuid,
          },
        },
        { requestPolicy: 'network-only' },
      )
      .toPromise();

    const count = data?.recurringEvents[0]?.futureEvents.length ?? 0;

    const result = await confirm({
      title: t('schoolEvents.deleteRecurring.title'),
      message: t('schoolEvents.deleteRecurring.warning', { eventCount: count }),
    });
    if (!result) return;
    setLoading(true);
    const { error } = await deleteRecurringEvent({ recurrenceUuid: recurrenceUuid }, context);
    if (error) {
      showUserErrorToast({ error });
    } else {
      showSuccessDeleteToast();
    }
    setLoading(false);
  }

  return (
    <Page isForbidden={!permissions?.canManageSubstitution()}>
      <SchoolEventTable
        loading={loading}
        onAdd={handleAdd}
        onEdit={handleEdit}
        onDuplicate={handleDuplicate}
        onDelete={handleDelete}
        onDeleteRecurring={handleDeleteRecurring}
      />

      <SchoolEventModal selectedEventUuid={selectedUuid} isOpen={modalOpen} onClose={() => setModalOpen(false)} />
      <ConfirmationDialog />
    </Page>
  );
});
